import {
  ActivePeriodsFilterConfig,
  ActivePeriodsFilterConfigColumn,
  AboveValueConfigKind,
  AvgSessionLengthFilterConfig,
  AvgSessionLengthFilterConfigColumn,
  CohortFilterGranularity,
  FirstInteractionFilterConfig,
  FirstInteractionFilterConfigColumn,
  InTheLastValueConfigKind,
  LastInteractionFilterConfig,
  LastInteractionFilterConfigColumn,
  LastInteractionTagFilterConfig,
  LastInteractionTagFilterConfigColumn,
  UserConversationLengthFilterConfig,
  UserConversationLengthFilterConfigColumn,
  UserConversationsCountFilterConfig,
  UserConversationsCountFilterConfigColumn,
  UsersInteractionsFilterConfig,
  UsersInteractionsFilterConfigColumn,
} from '@/api/generated/model';
import { FILTERS } from '@/models/common/filters';

export const COHORT_USER_FILTER_LABELS: Record<CohortUserFilterColumn, string> = {
  [FILTERS.firstInteraction]: 'First interaction',
  [FILTERS.lastInteraction]: 'Last interaction',
  [FILTERS.lastInteractionTag]: 'Interaction tag',
  [FILTERS.usersInteraction]: 'Number of interactions',
  [FILTERS.userConversationsCount]: 'Number of conversations',
  [FILTERS.userConversationLength]: 'Conversation turns',
  [FILTERS.activePeriods]: 'Active periods',
  [FILTERS.avgSessionLength]: 'Avg session length',
};

export const COHORT_USER_FILTER_SELECTABLE_COLUMNS = [
  FILTERS.firstInteraction,
  FILTERS.lastInteraction,
  FILTERS.lastInteractionTag,
  FILTERS.usersInteraction,
  FILTERS.userConversationsCount,
  FILTERS.activePeriods,
  FILTERS.avgSessionLength,
] as const;

export type CohortUserFilterSelectableColumn = (typeof COHORT_USER_FILTER_SELECTABLE_COLUMNS)[number];

export const COHORT_USER_FILTER_COLUMNS = [
  ...COHORT_USER_FILTER_SELECTABLE_COLUMNS,
  FILTERS.userConversationLength,
] as const;

export type CohortUserFilterColumn = (typeof COHORT_USER_FILTER_COLUMNS)[number];

export type CohortUserFilter =
  | FirstInteractionFilterConfig
  | LastInteractionFilterConfig
  | LastInteractionTagFilterConfig
  | UsersInteractionsFilterConfig
  | UserConversationsCountFilterConfig
  | UserConversationLengthFilterConfig
  | ActivePeriodsFilterConfig
  | AvgSessionLengthFilterConfig;

export function isCohortUserFilterColumn(column: string): column is CohortUserFilterColumn {
  return (COHORT_USER_FILTER_COLUMNS as readonly string[]).includes(column);
}

export function isCohortUserFilterSelectableColumn(column: string): column is CohortUserFilterSelectableColumn {
  return (COHORT_USER_FILTER_SELECTABLE_COLUMNS as readonly string[]).includes(column);
}

export function getCohortUserFilterSelectableColumns(currentColumn?: string): readonly CohortUserFilterColumn[] {
  if (currentColumn && isCohortUserFilterColumn(currentColumn) && !isCohortUserFilterSelectableColumn(currentColumn)) {
    return [...COHORT_USER_FILTER_SELECTABLE_COLUMNS, currentColumn];
  }

  return COHORT_USER_FILTER_SELECTABLE_COLUMNS;
}

export function isCohortUserFilter(value: unknown): value is CohortUserFilter {
  if (!value || typeof value !== 'object' || !('column' in value)) return false;
  return isCohortUserFilterColumn(String((value as { column: string }).column));
}

export function getCohortUserFilterLabel(column: string): string {
  if (isCohortUserFilterColumn(column)) {
    return COHORT_USER_FILTER_LABELS[column];
  }
  return column;
}

export function createDefaultSpecialTimeValues() {
  return {
    kind: InTheLastValueConfigKind.in_the_last,
    value: 1,
    granularity: CohortFilterGranularity.day,
  };
}

export function createDefaultUsersInteractionsValues(): UsersInteractionsFilterConfig['values'] {
  return {
    interactions_count: {
      kind: AboveValueConfigKind.above,
      value: 10,
    },
    dates: {
      kind: InTheLastValueConfigKind.in_the_last,
      value: 12,
      granularity: CohortFilterGranularity.week,
    },
  };
}

export function createDefaultUserConversationsCountValues(): UserConversationsCountFilterConfig['values'] {
  return {
    conversations_count: {
      kind: AboveValueConfigKind.above,
      value: 10,
    },
  };
}

export function createDefaultUserConversationLengthValues(): UserConversationLengthFilterConfig['values'] {
  return {
    interactions_count: {
      kind: AboveValueConfigKind.above,
      value: 5,
    },
  };
}

export function createDefaultActivePeriodsFilter(): ActivePeriodsFilterConfig {
  return {
    column: ActivePeriodsFilterConfigColumn.active_periods,
    values: {
      active_count: {
        kind: AboveValueConfigKind.above,
        value: 6,
      },
      window: 12,
      granularity: CohortFilterGranularity.week,
    },
  };
}

export function createDefaultAvgSessionLengthFilter(): AvgSessionLengthFilterConfig {
  return {
    column: AvgSessionLengthFilterConfigColumn.avg_session_length,
    values: {
      duration: {
        kind: AboveValueConfigKind.above,
        value: 120,
      },
      dates: {
        kind: InTheLastValueConfigKind.in_the_last,
        value: 12,
        granularity: CohortFilterGranularity.week,
      },
    },
  };
}

export function createDefaultCohortUserFilter(column: CohortUserFilterColumn): CohortUserFilter {
  switch (column) {
    case FILTERS.firstInteraction:
      return {
        column: FirstInteractionFilterConfigColumn.first_interaction,
        values: createDefaultSpecialTimeValues(),
      };
    case FILTERS.lastInteraction:
      return {
        column: LastInteractionFilterConfigColumn.last_interaction,
        values: createDefaultSpecialTimeValues(),
      };
    case FILTERS.lastInteractionTag:
      return {
        column: LastInteractionTagFilterConfigColumn.last_interaction_tag,
        tag: '',
        values: [],
      };
    case FILTERS.usersInteraction:
      return {
        column: UsersInteractionsFilterConfigColumn.users_interactions,
        values: createDefaultUsersInteractionsValues(),
      };
    case FILTERS.userConversationsCount:
      return {
        column: UserConversationsCountFilterConfigColumn.user_conversations_count,
        values: createDefaultUserConversationsCountValues(),
      };
    case FILTERS.userConversationLength:
      return {
        column: UserConversationLengthFilterConfigColumn.user_conversation_length,
        values: createDefaultUserConversationLengthValues(),
      };
    case FILTERS.activePeriods:
      return createDefaultActivePeriodsFilter();
    case FILTERS.avgSessionLength:
      return createDefaultAvgSessionLengthFilter();
    default: {
      const exhaustive: never = column;
      return exhaustive;
    }
  }
}

export function createDefaultCohortUserFilterLeaf(): CohortUserFilter {
  return createDefaultCohortUserFilter(FILTERS.firstInteraction);
}
